import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import zod from 'zod';
import { FormDataSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import usePermissionsApi from 'Hooks/usePermissionsApi';
import { PermissionWithAssignedT, RoleWithPermissionsI } from 'types';
import rolesApi from 'Api/axios/roles-api';
import useModal from 'Hooks/useModal';
import useTranslation from 'Hooks/useTranslation';
import { toast } from 'sonner';
import { useHistory } from 'react-router-dom';

/**
 * Type para traducción, se hace de esta manera por la deficiencia
 * del custom hook de "useTranslation".
 */
export interface Translation {
  title: string;
  subtitle: string;
  modal: {
    title: string;
    content: string;
  };
  success: string;
  error: string;
  name: string;
  status: string;
  default_alert: string;
  name_empty: string;
  rol_status_empty: string;
  permissions_empty: string;
  save: string;
  cancel: string;
  active: string;
  inactive: string;
}

const defaultTranslation = {
  title: '',
  subtitle: '',
  modal: {
    title: '',
    content: '',
  },
  success: '',
  error: '',
  name: '',
  status: '',
  default_alert: '',
  name_empty: '',
  rol_status_empty: '',
  permissions_empty: '',
  save: '',
  cancel: '',
  active: '',
  inactive: '',
};

type FormData = zod.infer<typeof FormDataSchema>;

const defaultValues: FormData = {
  name: '',
  active: true,
  permissions: [],
};

interface Props {
  id?: string;
}

/**
 * @overview Custom hook para manejar la lógica de creación
 * de roles en la sección "Gestión de Tipos de usuarios".
 */
export const useRolesCreatePage = ({ id = null }: Props = {}) => {
  // Archivo de traducciones
  const { t2 } = useTranslation('role_permissions');

  // Manejo de rutas
  const history = useHistory();

  // Custom hook para obtener los permisos del sistema.
  const { data: allPermissions, fetchPermissions } = usePermissionsApi();

  // Estado para manejar la lista de permisos que mostraremos en pantalla.
  const [permissions, setPermissions] = useState<PermissionWithAssignedT>([]);

  // Maneja el estado del modal de confirmación
  const { open, openModal, closeModal } = useModal();

  // Variable de traducción para manejarlo correctamente
  const [translation, setTranslation] = useState<Translation>(defaultTranslation);

  // Estado para guardar el nombre del rol.
  const form = useForm<FormData>({
    resolver: zodResolver(FormDataSchema),
    defaultValues,
  });
  const { register, formState, handleSubmit, setValue, getValues, watch } = form;

  /**
   * Observa los cambios en el id del rol para identificar
   * si se esta creando un nuevo rol o actualizando.
   *
   * De esta manera obtiene los datos actuales del rol.
   */
  useEffect(() => {
    if (id !== null && permissions.length === 0) {
      // Si existe ID, consultamos los permisos del rol.
      // Este endpoint, ya trae todos los permisos del sistema
      // indicando cual tiene assignado el rol.
      rolesApi.readPermissions(id).then(response => {
        if (response.data) {
          setValue('name', response.data.name);
          setValue('active', response.data.active);
          setPermissions(response.data.permissions);

          // Sincronizar el campo 'permissions' del formulario con los permisos asignados
          const assignedPermissions = response.data.permissions.filter(p => p.assigned).map(p => ({ id: p.id }));

          setValue('permissions', assignedPermissions);
        }
      });
    } else if (permissions.length === 0) {
      // Caso contrario, obtenemos todos los roles disponibles en el sistema.
      fetchPermissions();
    }
  }, [id]);

  /**
   * Escuchamos los cambios de "allPermissions" para ajustarlos
   * a la vista que mostraremos.
   */
  useEffect(() => {
    if (allPermissions) {
      const draft: PermissionWithAssignedT = [];
      allPermissions.forEach(permission => {
        draft.push({
          ...permission,
          assigned: false,
        });
      });
      setPermissions(draft);
    }
  }, [allPermissions]);

  // Actualiza la variable de tradicciones
  useEffect(() => {
    setTranslation({
      title: t2(id ? 'edit.title' : 'create.title'),
      subtitle: t2(id ? 'edit.subtitle' : 'create.subtitle'),
      modal: {
        title: t2(id ? 'edit.modal.title' : 'create.modal.title'),
        content: t2(id ? 'edit.modal.content' : 'create.modal.content'),
      },
      success: t2(id ? 'edit.success' : 'create.success'),
      error: t2(id ? 'edit.error' : 'create.error'),
      name: t2('name'),
      status: t2('status'),
      default_alert: t2('default_alert'),
      name_empty: t2('name_empty'),
      rol_status_empty: t2('rol_status_empty'),
      permissions_empty: t2('permissions_empty'),
      save: t2('save'),
      cancel: t2('cancel'),
      active: t2('active'),
      inactive: t2('inactive'),
    });
  }, [id]);

  /**
   * Actualiza la selección al assignar o quitar un permiso
   */
  const onChangeChecked = (id: string, status: boolean) => {
    // Actualiza el estado visual de los permisos
    const updatedPermissions = permissions.map(p => (p.id === id ? { ...p, assigned: status } : p));
    setPermissions(updatedPermissions);

    // Actualiza el valor del campo 'permissions' en el formulario
    const currentPermissions = getValues('permissions');
    let newPermissions;
    if (status) {
      // Agrega el permiso si no existe
      newPermissions = [...currentPermissions, { id }];
    } else {
      // Elimina el permiso
      newPermissions = currentPermissions.filter(p => p.id !== id);
    }
    setValue('permissions', newPermissions, { shouldValidate: true });
  };

  /**
   * Envía el formulario para crear o actualizar
   * el rol.
   */
  const submit = async (formData: FormData) => {
    try {
      const response = id !== null ? await rolesApi.patch(id, formData) : await rolesApi.createRole(formData);

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(translation.success);
      closeModal();
      history.goBack();
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formState,
    register,
    submit: handleSubmit(submit),
    permissions,

    // Modal confirmación
    open,
    openModal,
    closeModal,

    onChangeChecked,
    translation,
    t2,
    getValues,
    setValue,
    watch,
  };
};
